import { EIXOS } from '../constants/eixos';

export const RADAR_REDE_FEDERAL_EIXOS = [
  { id: 'iGovPub', label: 'Governança Pública' },
  { id: 'iGovSustentSocial', label: 'Sustentabilidade Social' },
  { id: 'iGovSustentAmb', label: 'Sustentabilidade Ambiental' },
  { id: 'iGovPessoas', label: 'Governança de Pessoas' },
  { id: 'iGovTI', label: 'Governança de TI' },
  { id: 'iGovContratações', label: 'Governança de Contratações' },
  { id: 'iGovOrcament', label: 'Governança Orçamentária' },
];

export const normalizeRadarValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.max(0, Math.min(1, number > 1 ? number / 100 : number));
};

export const formatRadarPercent = (value) => {
  const normalized = normalizeRadarValue(value);
  if (normalized === null) return '-';
  return `${(normalized * 100).toFixed(1).replace('.', ',')}%`;
};

export const buildRedeFederalRadarData = ({ indicadores = {}, benchmark = {}, sigla = 'Instituição', benchmarkLabel = 'Média da Rede Federal' }) => {
  const labels = [];
  const instituicaoValues = [];
  const benchmarkValues = [];

  RADAR_REDE_FEDERAL_EIXOS.forEach((eixo) => {
    const instValue = normalizeRadarValue(indicadores[eixo.id]);
    const benchValue = normalizeRadarValue(benchmark[eixo.id]);
    if (instValue === null || benchValue === null) return;
    labels.push(eixo.label);
    instituicaoValues.push(instValue);
    benchmarkValues.push(benchValue);
  });

  return {
    labels,
    datasets: [
      {
        label: sigla,
        data: instituicaoValues,
        backgroundColor: 'rgba(0, 168, 232, 0.24)',
        borderColor: '#00A8E8',
        pointBackgroundColor: '#00A8E8',
        pointBorderColor: '#fff',
        borderWidth: 2,
        fill: true,
      },
      {
        label: benchmarkLabel,
        data: benchmarkValues,
        backgroundColor: 'rgba(100, 116, 139, 0)',
        borderColor: '#64748b',
        pointBackgroundColor: '#64748b',
        pointBorderColor: '#fff',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
      },
    ],
  };
};

export const buildRedeFederalAverageRadarData = ({ medias = {}, label = 'Média da Rede Federal' }) => {
  const labels = [];
  const values = [];

  RADAR_REDE_FEDERAL_EIXOS.forEach((eixo) => {
    const value = normalizeRadarValue(medias[eixo.id]);
    if (value === null) return;
    labels.push(eixo.label);
    values.push(value);
  });

  return {
    labels,
    datasets: [
      {
        label,
        data: values,
        backgroundColor: 'rgba(29, 78, 216, 0.22)',
        borderColor: '#1d4ed8',
        pointBackgroundColor: '#1d4ed8',
        pointBorderColor: '#fff',
        borderWidth: 2,
        fill: true,
      },
    ],
  };
};

const mapMediasPorEixo = (mediasPorEixo) => {
  if (!mediasPorEixo) return {};
  if (Array.isArray(mediasPorEixo)) {
    return mediasPorEixo.reduce((acc, item) => {
      if (item?.eixo) acc[item.eixo] = normalizeRadarValue(item.media);
      return acc;
    }, {});
  }
  return Object.entries(mediasPorEixo).reduce((acc, [eixo, value]) => {
    acc[eixo] = normalizeRadarValue(value);
    return acc;
  }, {});
};

export const buildCycleRadarData = ({ currentMediasPorEixo = [], historico = [], maxHistorico = 3 }) => {
  const currentMap = mapMediasPorEixo(currentMediasPorEixo);
  const cycleMaps = [
    ...historico
      .filter((item) => item?.mediasPorEixo)
      .slice(-maxHistorico)
      .map((item) => ({
        label: item.rotulo || item.ciclo || item.dataSnapshot?.toLocaleDateString?.('pt-BR') || 'Ciclo anterior',
        values: mapMediasPorEixo(item.mediasPorEixo),
        past: true,
      })),
    { label: 'Ciclo atual', values: currentMap, past: false },
  ];

  const compatibleEixos = EIXOS
    .map((eixo) => eixo.label)
    .filter((label) => cycleMaps.every((cycle) => cycle.values[label] !== null && cycle.values[label] !== undefined));

  if (compatibleEixos.length < 3) {
    return { labels: [], datasets: [] };
  }

  const pastColors = ['#334155', '#475569', '#0369a1'];
  return {
    labels: compatibleEixos,
    datasets: cycleMaps.map((cycle, index) => {
      const color = cycle.past ? pastColors[index % pastColors.length] : '#f97316';
      return {
        label: cycle.label,
        data: compatibleEixos.map((label) => cycle.values[label]),
        backgroundColor: cycle.past ? 'rgba(51, 65, 85, 0.08)' : 'rgba(249, 115, 22, 0.22)',
        borderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        borderWidth: cycle.past ? 1.8 : 2.4,
        fill: !cycle.past,
      };
    }),
  };
};

export const buildTwoCycleRadarData = ({ currentMediasPorEixo = [], baselineMediasPorEixo = null, baselineLabel = 'Ciclo TCO 2024' }) => {
  if (!baselineMediasPorEixo) return { labels: [], datasets: [] };

  const currentMap = mapMediasPorEixo(currentMediasPorEixo);
  const baselineMap = mapMediasPorEixo(baselineMediasPorEixo);
  const compatibleEixos = EIXOS
    .map((eixo) => eixo.label)
    .filter((label) => currentMap[label] !== null && currentMap[label] !== undefined && baselineMap[label] !== null && baselineMap[label] !== undefined);

  if (compatibleEixos.length < 3) {
    return { labels: [], datasets: [] };
  }

  return {
    labels: compatibleEixos,
    datasets: [
      {
        label: baselineLabel,
        data: compatibleEixos.map((label) => baselineMap[label]),
        backgroundColor: 'rgba(51, 65, 85, 0.08)',
        borderColor: '#334155',
        pointBackgroundColor: '#334155',
        pointBorderColor: '#fff',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
      },
      {
        label: 'Ciclo atual',
        data: compatibleEixos.map((label) => currentMap[label]),
        backgroundColor: 'rgba(249, 115, 22, 0.22)',
        borderColor: '#f97316',
        pointBackgroundColor: '#f97316',
        pointBorderColor: '#fff',
        borderWidth: 2.4,
        fill: true,
      },
    ],
  };
};

export const calculateTcu2024MediasPorEixo = ({ orgBenchmark, questoes = [] }) => {
  if (!orgBenchmark?.respostas || !Array.isArray(questoes) || questoes.length === 0) return null;

  const scoreFromValor = (valor) => {
    if (valor === 8) return 0.85;
    if (valor === 7) return 0.5;
    if (valor === 6) return 0.15;
    if (valor === 5) return 0.05;
    return 0;
  };

  const grouped = {};
  EIXOS.forEach((eixo) => {
    grouped[eixo.label] = { soma: 0, contagem: 0 };
  });

  questoes.forEach((questao) => {
    if (!grouped[questao.eixo]) return;
    const qKey = questao.codigo?.startsWith('Q') ? questao.codigo : `Q${questao.codigo}`;
    const resposta = orgBenchmark.respostas[qKey];
    if (!resposta || resposta.valor < 4 || resposta.valor > 8) return;
    grouped[questao.eixo].soma += scoreFromValor(resposta.valor);
    grouped[questao.eixo].contagem += 1;
  });

  const result = {};
  Object.entries(grouped).forEach(([eixo, data]) => {
    if (data.contagem > 0) result[eixo] = data.soma / data.contagem;
  });

  return Object.keys(result).length >= 3 ? result : null;
};

export const buildSegmentBenchmarkRadarData = ({ indicadores = {}, benchmark = {}, orgLabel = 'Organização', benchmarkLabel = 'Média do segmento' }) => (
  buildRedeFederalRadarData({
    indicadores,
    benchmark,
    sigla: orgLabel,
    benchmarkLabel,
  })
);
